<script lang="ts" setup>
export interface ConfirmDialogProps {
  cancelText?: string;
  confirmColor?: string;
  confirmText?: string;
  /** Whether confirmation is disabled/pending */
  disabled?: boolean;
  modelValue: boolean;
  /**
   * Whether dialog should remain open upon "confirm" click (for manual closing after async confirmation)
   *
   * By default, the `update:modelValue` event is emitted immediately upon "confirm" click.
   */
  noCloseOnConfirm?: boolean;
  loading?: boolean;
  persistent?: boolean;
  title: string;
  /**
   * Confirmation dialog type
   *
   * - "confirm" - Confirm/cancel buttons
   * - "alert" - Confirm button
   */
  type?: "confirm" | "alert";
}

const props = withDefaults(defineProps<ConfirmDialogProps>(), {
  cancelText: undefined,
  confirmColor: "primary",
  confirmText: undefined,
  disabled: false,
  loading: false,
  type: "confirm",
});

const emit = defineEmits<{
  (event: "update:modelValue", open: boolean): void;
  (event: "confirm"): void;
  (event: "cancel"): void;
}>();

const handleCancel = () => {
  emit("cancel");
  emit("update:modelValue", false);
};

const handleConfirm = () => {
  emit("confirm");

  // Confirmation dialogs might not always close immediately after confirmation (ie. operation pending, etc)
  if (!props.noCloseOnConfirm) {
    emit("update:modelValue", false);
  }
};

/** Handle dialog closure events (ie. `esc` key, clicking outside modal, etc) */
const handleModelUpdate = (open: boolean) => {
  if (!open && !props.persistent) {
    // NOTE: Cancellation function already emits model value update event
    handleCancel();
  }
};
</script>

<template>
  <VDialog
    max-width="400"
    min-width="300"
    :model-value="modelValue"
    :persistent="persistent"
    v-bind="$attrs"
    @update:model-value="handleModelUpdate"
  >
    <VCard>
      <VCardTitle>{{ title }}</VCardTitle>
      <VCardText v-if="$slots.default">
        <slot />
      </VCardText>
      <VCardActions>
        <ActionBar right>
          <VBtn v-if="type === 'confirm'" :disabled="loading" @click="handleCancel">
            {{ cancelText ?? "Cancel" }}
          </VBtn>
          <VBtn
            :color="confirmColor"
            :disabled="disabled"
            :loading="loading"
            variant="tonal"
            @click="handleConfirm"
          >
            {{ confirmText ?? "Confirm" }}
          </VBtn>
        </ActionBar>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
// NOTE: Fix for dialog overlay not expanding (needed to expand smaller dialog content)
/*.v-dialog :deep(.v-overlay__content) {
  width: 100%;
}*/

.v-dialog .v-card-text {
  padding: spacing(2) spacing(4) !important;
  white-space: break-spaces;
}
</style>
